import { t } from '../../i18n';
// admin-client/src/pages/Admin/AgentTreePage.jsx
import React from 'react';
import api from '../../api/axios';
import AgentTreeNode from '../../components/AgentTreeNode';
import AgentFeeDefaultModal from '../../components/AgentFeeDefaultModal';
const DBG = typeof window !== 'undefined'
    ? (window.__DEBUG_ADMIN_TREE__ ?? (process.env.NODE_ENV !== 'production'))
    : true;
const dlog = (...args) => { if (DBG)
    console.log('%c[AgentTreePage]', 'color:#6b7280;font-weight:600', ...args); };
function refreshAdminAssetSummary() {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('admin:asset-summary-refresh'));
    }
}
// 트리 안에서 특정 agent_id의 asset을 응답 값으로 부분 패치
function patchAssetsInTree(tree, { src_user_code, src_asset, dst_user_code, dst_asset }) {
    if (!tree)
        return tree;
    const S = String;
    const visit = (n) => {
        let changed = false;
        let next = n;
        // 송신자(감소 주체) 반영: 값이 null/undefined면 건드리지 않음
        if (src_user_code != null && src_asset != null && S(n.agent_id) === S(src_user_code)) {
            next = { ...next, asset: src_asset };
            changed = true;
        }
        // 수신자(증가 주체) 반영
        if (dst_user_code != null && dst_asset != null && S(n.agent_id) === S(dst_user_code)) {
            next = { ...next, asset: dst_asset };
            changed = true;
        }
        // 🔹 유저 배열 자산 패치도 수행
        if (n.users && n.users.length) {
            const newUsers = n.users.map(u => {
                let uu = u, uc = false;
                if (src_user_code != null && src_asset != null && S(u.user_code) === S(src_user_code)) {
                    uu = { ...uu, asset: src_asset };
                    uc = true;
                }
                if (dst_user_code != null && dst_asset != null && S(u.user_code) === S(dst_user_code)) {
                    uu = { ...uu, asset: dst_asset };
                    uc = true;
                }
                return uc ? uu : u;
            });
            if (newUsers.some((u, i) => u !== n.users[i])) {
                next = { ...next, users: newUsers };
                changed = true;
            }
        }
        if (n.children && n.children.length) {
            const newChildren = n.children.map(visit);
            if (newChildren.some((c, i) => c !== n.children[i])) {
                next = { ...next, children: newChildren };
                changed = true;
            }
        }
        return changed ? next : n;
    };
    return visit(tree);
}
export default function AgentTreePage() {
    const [tree, setTree] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [feeDefaultStatus, setFeeDefaultStatus] = React.useState(null);
    const [feeDefaultLoading, setFeeDefaultLoading] = React.useState(false);
    const [feeDefaultOpen, setFeeDefaultOpen] = React.useState(false);
    const loadTree = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.get('/admin/users/tree');
            setTree(res.data?.tree ?? null);
            dlog('loaded tree', res.data);
        }
        catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
        finally {
            setLoading(false);
        }
    }, []);
    const loadFeeDefaultPolicy = React.useCallback(async ({ autoOpen = false } = {}) => {
        setFeeDefaultLoading(true);
        try {
            const res = await api.get('/admin/agents/fee-default-policy');
            const nextStatus = res.data || null;
            setFeeDefaultStatus(nextStatus);
            if (autoOpen && nextStatus?.needs_configuration) {
                setFeeDefaultOpen(true);
            }
            return nextStatus;
        }
        catch (e) {
            dlog('fee default policy load failed', e?.response?.data || e.message);
            return null;
        }
        finally {
            setFeeDefaultLoading(false);
        }
    }, []);
    React.useEffect(() => {
        loadTree();
        loadFeeDefaultPolicy({ autoOpen: true });
    }, [loadTree, loadFeeDefaultPolicy]);

    const handleSaveFeeDefaultPolicy = React.useCallback(async (policy) => {
        const res = await api.put('/admin/agents/fee-default-policy', { policy });
        setFeeDefaultStatus(res.data || null);
        setFeeDefaultOpen(false);
        return res.data;
    }, []);
    // 하위 생성
    const handleCreate = React.useCallback(async (form) => {
        try {
            await api.post('/admin/users/create', form);
            await loadTree();
        }
        catch (e) {
            alert(e?.response?.data?.message || e.message);
        }
    }, [loadTree]);
    // 유저 차단/해제
    const handleBlock = React.useCallback(async (target_id, is_banned) => {
        try {
            await api.post('/admin/users/block', { target_id, is_banned });
            await loadTree();
        }
        catch (e) {
            alert(e?.response?.data?.message || e.message);
        }
    }, [loadTree]);
    // 계정 삭제 (Superadmin은 전체 하위, 일반 관리자는 직속 하위)
    const handleDelete = React.useCallback(async (target_id, meta = {}) => {
        const accountType = meta?.type === 'agent' ? t('에이전트') : t('유저');
        const displayLabel = meta?.label && String(meta.label) !== String(target_id)
            ? `${meta.label} (${target_id})`
            : String(target_id);
        const message = `${accountType} ${displayLabel}\n${t('삭제')}?`;
        if (!window.confirm(message)) return;
        try {
            await api.post('/admin/users/delete', { target_id });
            await loadTree();
        }
        catch (e) {
            alert(e?.response?.data?.message || e.message);
        }
    }, [loadTree]);
    // 수수료 수정 (에이전트)
    const handleUpdateFee = React.useCallback(async (agent_id, nextFee) => {
        try {
            // 필요 시 백엔드 라우트 구현(/admin/agents/fee)
            await api.post('/admin/agents/fee', { agent_id, fee_percent: nextFee });
            await loadTree();
        }
        catch (e) {
            alert(e?.response?.data?.message || e.message || t("\uC218\uC218\uB8CC \uC218\uC815 \uC2E4\uD328"));
        }
    }, [loadTree]);
    // 🔹 지급: 부분 패치로 즉시 반영
    const handleCredit = React.useCallback(async (targetId, amount) => {
        try {
            const { data } = await api.post('/admin/users/asset/credit', { target_id: targetId, amount });
            if (!data?.ok || data?.no_op) {
                throw new Error(data?.message || data?.reason || t("\uC9C0\uAE09\uC774 \uCC98\uB9AC\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4."));
            }
            // 기대 응답: { ok, minted, src_user_code, src_asset, dst_user_code, dst_asset }
            setTree(prev => patchAssetsInTree(prev, {
                src_user_code: data.src_user_code,
                src_asset: data.src_asset, // superadmin 민팅이면 null일 수 있음 → patch에서 무시
                dst_user_code: data.dst_user_code,
                dst_asset: data.dst_asset,
            }));
            refreshAdminAssetSummary();
        }
        catch (e) {
            alert(e?.response?.data?.message || e.message || t("\uC9C0\uAE09 \uC2E4\uD328"));
        }
    }, []);
    // 🔹 차감: 부분 패치로 즉시 반영
    const handleDebit = React.useCallback(async (targetId, amount) => {
        try {
            const { data } = await api.post('/admin/users/asset/debit', { target_id: targetId, amount });
            if (!data?.ok || data?.no_op) {
                throw new Error(data?.message || data?.reason || t("\uCC28\uAC10\uC774 \uCC98\uB9AC\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4."));
            }
            setTree(prev => patchAssetsInTree(prev, {
                src_user_code: data.src_user_code,
                src_asset: data.src_asset,
                dst_user_code: data.dst_user_code,
                dst_asset: data.dst_asset,
            }));
            refreshAdminAssetSummary();
        }
        catch (e) {
            alert(e?.response?.data?.message || e.message || t("\uCC28\uAC10 \uC2E4\uD328"));
        }
    }, []);
    return (
    <div className="admin-content-page p-2 sm:p-3">
      <div className="mx-auto w-full max-w-screen-2xl">
        <div className="admin-page-head">
          <div>
            <p className="admin-page-eyebrow">USER MANAGEMENT</p>
            <h1>{t("\uC720\uC800 \uAD00\uB9AC")}</h1>
            <p className="admin-page-description">{t('에이전트 계층과 소속 유저, 수수료, 자산 및 미션 상금 정책을 관리합니다.')}</p>
          </div>
          <div className="admin-page-head-actions">
            <button
              type="button"
              onClick={() => setFeeDefaultOpen(true)}
              disabled={feeDefaultLoading}
              className="admin-primary-button"
            >
              {feeDefaultStatus?.needs_configuration ? t('수수료 기본값 설정') : t('수수료 기본값 수정')}
            </button>
            <button type="button" onClick={loadTree} disabled={loading} className="admin-secondary-button">
              {loading ? t("로딩 중...") : t("새로고침")}
            </button>
          </div>
        </div>

        {loading && <div className="text-sm text-gray-500">{t("\uB85C\uB529 \uC911...")}</div>}
        {error && <div className="text-sm text-red-600">{error}</div>}

        {tree ? (<AgentTreeNode node={tree} isRoot
        viewerIsSuperadmin={String(tree.agent_id) === '10000000' && Number(tree.level) === 1}
        // ↓ 기존 핸들러 유지
        onCreate={handleCreate} onBlock={handleBlock} onDelete={handleDelete} onUpdateFee={handleUpdateFee} 
        // ↓ 자산 변경 즉시 반영(부분 패치)
        onCredit={handleCredit} onDebit={handleDebit}/>) : (!loading && <div className="text-sm text-gray-400">{t("\uB370\uC774\uD130\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.")}</div>)}
      </div>

      <AgentFeeDefaultModal
        open={feeDefaultOpen}
        status={feeDefaultStatus}
        onClose={() => setFeeDefaultOpen(false)}
        onSaved={handleSaveFeeDefaultPolicy}
      />
    </div>);
}
